import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  FLIPKART_BUDGET_TERMINAL_STATUSES,
  parseFlipkartCampaignBudgetJob,
  parseFlipkartCampaignBudgetResult,
  type FlipkartBudgetAdjustMode,
  type FlipkartCampaignBudgetJob,
  type FlipkartCampaignBudgetResult,
} from '@/api/flipkartCampaignBudgetJob';
import { flipkartAdsApi } from '@/api/client';

const POLL_MS = 2000;

function markPollFailed(
  setJob: (updater: (current: FlipkartCampaignBudgetJob | null) => FlipkartCampaignBudgetJob | null) => void,
  error: unknown
) {
  const message = error instanceof Error && error.message ? error.message : 'Budget job failed';
  setJob((current) =>
    current
      ? {
          ...current,
          status: 'failed',
          applyProgress: { ...current.applyProgress, error: message },
        }
      : current
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function pollJobUntilDone(
  credentialId: string,
  jobId: string,
  onUpdate: (job: FlipkartCampaignBudgetJob) => void,
  abortRef: { current: boolean }
): Promise<FlipkartCampaignBudgetJob> {
  for (;;) {
    const res = await flipkartAdsApi.getCampaignBudgetJob(credentialId, jobId);
    const job = parseFlipkartCampaignBudgetJob(res.data);
    if (!job) throw new Error('Invalid budget job response');
    onUpdate(job);
    if (FLIPKART_BUDGET_TERMINAL_STATUSES.has(job.status)) return job;
    if (abortRef.current) return job;
    await sleep(POLL_MS);
  }
}

export function useFlipkartCampaignBudgetJob(credentialId: string, adAccountId: string) {
  const [job, setJob] = useState<FlipkartCampaignBudgetJob | null>(null);
  const [failedResults, setFailedResults] = useState<FlipkartCampaignBudgetResult[]>([]);
  const abortRef = useRef(false);

  useEffect(() => {
    abortRef.current = false;
    setJob(null);
    setFailedResults([]);
    return () => {
      abortRef.current = true;
    };
  }, [adAccountId, credentialId]);

  const loadFailedResults = useCallback(
    async (jobId: string) => {
      const res = await flipkartAdsApi.listCampaignBudgetJobResults(credentialId, jobId, {
        status: 'failed',
        limit: 100,
        offset: 0,
      });
      const raw = (res.data as { results?: unknown[] } | null)?.results ?? [];
      setFailedResults(
        raw
          .map((item) => parseFlipkartCampaignBudgetResult(item))
          .filter((item): item is FlipkartCampaignBudgetResult => item != null)
      );
    },
    [credentialId]
  );

  const startMutation = useMutation({
    mutationFn: async (input: {
      adAccountId: string;
      campaignIds: string[];
      adjustMode: FlipkartBudgetAdjustMode;
      adjustValue: number;
    }) => {
      const createRes = await flipkartAdsApi.createCampaignBudgetJob(credentialId, {
        ad_account_id: input.adAccountId,
        campaign_ids: input.campaignIds,
        adjust_mode: input.adjustMode,
        adjust_value: input.adjustValue,
      });
      const created = parseFlipkartCampaignBudgetJob(createRes.data);
      if (!created) throw new Error('Failed to start budget job');
      setJob(created);
      setFailedResults([]);
      if (FLIPKART_BUDGET_TERMINAL_STATUSES.has(created.status)) {
        if ((created.totals.failed ?? 0) > 0) await loadFailedResults(created.id);
        return created;
      }
      try {
        const finalJob = await pollJobUntilDone(credentialId, created.id, setJob, abortRef);
        if ((finalJob.totals.failed ?? 0) > 0) await loadFailedResults(finalJob.id);
        return finalJob;
      } catch (error) {
        markPollFailed(setJob, error);
        throw error;
      }
    },
  });

  const retryMutation = useMutation({
    mutationFn: async (jobId: string) => {
      const retryRes = await flipkartAdsApi.retryCampaignBudgetJob(credentialId, jobId);
      const restarted = parseFlipkartCampaignBudgetJob(retryRes.data);
      if (!restarted) throw new Error('Failed to retry budget job');
      setJob(restarted);
      setFailedResults([]);
      try {
        const finalJob = await pollJobUntilDone(credentialId, restarted.id, setJob, abortRef);
        if ((finalJob.totals.failed ?? 0) > 0) await loadFailedResults(finalJob.id);
        return finalJob;
      } catch (error) {
        markPollFailed(setJob, error);
        throw error;
      }
    },
  });

  const isRunning =
    startMutation.isPending ||
    retryMutation.isPending ||
    (job != null && !FLIPKART_BUDGET_TERMINAL_STATUSES.has(job.status));

  return {
    job,
    failedResults,
    isRunning,
    start: startMutation.mutateAsync,
    retryFailed: retryMutation.mutateAsync,
    reset: () => {
      setJob(null);
      setFailedResults([]);
      startMutation.reset();
      retryMutation.reset();
    },
    error: startMutation.error ?? retryMutation.error,
  };
}
